import type {
  AcceptInvitationRequest,
  AcceptInvitationSignedInRequest,
  AcceptMyInvitationRequest,
  CreateInvitationRequest,
  InvitationPreview,
  InvitationStatus,
  InvitationView,
  ListCabinetRolesResult,
  ListInvitationsResult,
  ListMyInvitationsResult,
} from "@planerius/contracts/cabinet-auth"
import { InvitationPreview as InvitationPreviewSchema } from "@planerius/contracts/cabinet-auth"

import { VALIDATE_RESPONSES } from "./config"
import { apiRequest } from "./http"
import { ApiProblem } from "./problem"

/**
 * Приглашения сотрудников: экран по ссылке (1-Э1.5) и окно на «Сотрудниках».
 *
 * СЕССИЯ ПОСЛЕ ПРИНЯТИЯ НЕ ВЫДАЁТСЯ (access.controller.ts): новый человек идёт на обычный
 * вход с паролем, который только что задал. Так вход всегда один, и «принял приглашение —
 * сразу внутри» не становится вторым, менее проверенным путём в кабинет.
 */

/** Экран по ссылке — до входа. Схемой проверяется в разработке: экран решает по полям ответа. */
export async function previewInvitation(token: string, signal?: AbortSignal): Promise<InvitationPreview> {
  const body = await apiRequest<unknown>("/cabinet/invitations/preview", {
    query: { token },
    authenticated: false,
    signal,
  })
  if (!VALIDATE_RESPONSES) return body as InvitationPreview
  const parsed = InvitationPreviewSchema.safeParse(body)
  if (!parsed.success) {
    throw new ApiProblem({
      code: "bad_response",
      status: 200,
      title: "Ответ сервиса не соответствует контракту",
      detail: `invitations/preview: ${parsed.error.message}`,
    })
  }
  return parsed.data
}

/** Принятие новым человеком: заводит вход. */
export function acceptInvitation(request: AcceptInvitationRequest): Promise<{ readonly status: "accepted" }> {
  return apiRequest("/cabinet/invitations/accept", { method: "POST", body: request, authenticated: false })
}

/** Принятие вошедшим: салон добавляется к его учётке. Ответ называет салон — в него и переходим. */
export function acceptInvitationSignedIn(
  request: AcceptInvitationSignedInRequest,
): Promise<{ readonly status: "accepted"; readonly tenantId: string }> {
  return apiRequest("/cabinet/invitations/accept-signed-in", { method: "POST", body: request })
}

/**
 * Выпуск приглашения. Ссылка приходит ОДИН раз — в этом ответе: в базе только её хеш,
 * и показать её повторно нельзя, только выпустить новую (resend).
 */
export function createInvitation(
  request: CreateInvitationRequest,
): Promise<{ readonly invitation: InvitationView; readonly link: string }> {
  return apiRequest("/cabinet/invitations", { method: "POST", body: request })
}

/** Висящие приглашения. limit в запрос не передаётся: сервер ждёт число, а строка запроса — строки. */
export function listInvitations(status: InvitationStatus, signal?: AbortSignal): Promise<ListInvitationsResult> {
  return apiRequest("/cabinet/invitations", { query: { status }, signal })
}

/** Повторная отправка — новая ссылка; прежняя перестаёт работать. */
export function resendInvitation(
  invitationId: string,
): Promise<{ readonly invitation: InvitationView; readonly link: string }> {
  return apiRequest("/cabinet/invitations/resend", { method: "POST", body: { invitationId } })
}

export function revokeInvitation(invitationId: string, reason?: string): Promise<void> {
  return apiRequest("/cabinet/invitations/revoke", { method: "POST", body: { invitationId, reason } })
}

/** Роли салона с признаком «можно выдать» — по тому же правилу, что проверяет выпуск. */
export function listRoles(signal?: AbortSignal): Promise<ListCabinetRolesResult> {
  return apiRequest("/cabinet/roles", { signal })
}

/** Приглашения, адресованные вошедшему, — строки экрана выбора салона (1-Э1.4). */
export function listMyInvitations(signal?: AbortSignal): Promise<ListMyInvitationsResult> {
  return apiRequest("/cabinet/invitations/mine", { signal })
}

/** Принятие из списка салонов — без ссылки из сообщения; согласия те же два. */
export function acceptMyInvitation(
  request: AcceptMyInvitationRequest,
): Promise<{ readonly status: "accepted"; readonly tenantId: string }> {
  return apiRequest("/cabinet/invitations/accept-mine", { method: "POST", body: request })
}
